import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CountryInterface } from '../../../../../shared/interfaces/create-surveys.interface';
import { BuyerSurveyResponse } from '../../../../../shared/interfaces/survey.interface';
import {
  McSpvSurveyRequest,
  MultiCountrySpvSurvey,
  MultiCountrySurvey,
} from '../interface/multi-country.interface';
import { AuthService } from '../../../../../shared/services/auth/auth.service';
import { Router } from '@angular/router';
import { Subscription, merge, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { CommonService } from '../../../../../shared/services/common-service/common-service';
import { deepCopy } from '../../../../utils/deep-copy';
import { SurveyPayload } from '../../../../../shared/interfaces/survey-meta-data.interface';

@Component({
  selector: 'ps-ui-multi-country-draft',
  templateUrl: './multi-country-draft.component.html',
  styleUrls: ['./multi-country-draft.component.css'],
})
export class MultiCountryDraftComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() surveyDetails?: BuyerSurveyResponse;
  @Input() surveyPayload = <SurveyPayload>{};
  @Input() mcSvpPayload = <McSpvSurveyRequest>{};
  @Input() countries: CountryInterface[] = [];
  @Input() selectedTemplateId?: number | null;
  @Input() multiCountrySurveyData: MultiCountrySurvey[] = [];
  @Input() multiCountrySpvParentSurvey = <BuyerSurveyResponse>{};
  @Input() multiCountrySpvChildSurveys: MultiCountrySpvSurvey[] = [];

  @Output() readonly selectionChange = new EventEmitter();
  @Output() mcSurveyAdded = new EventEmitter();
  @Output() updateMultiCountrySurveyData = new EventEmitter<
    MultiCountrySurvey[]
  >();
  @Output() updateMcSpvChildSurveysData = new EventEmitter<
    MultiCountrySpvSurvey[]
  >();
  @Output() cancelTemplateSelection = new EventEmitter();

  private _subscriptions: Subscription = new Subscription();
  private _multiCountrySurveyDataCommand$ = new EventEmitter<
    MultiCountrySurvey[]
  >();
  private _multiCountrySpvChildSurveysCommand$ = new EventEmitter<
    MultiCountrySpvSurvey[]
  >();

  public isCpiEditable = false;
  public filteredCountries: CountryInterface[] = [];

  constructor(
    private _auth: AuthService,
    private _router: Router,
    private _commonService: CommonService
  ) {
    this._handleFilterCountries(); // Adding it here because this component initializes on mc survey data and filtering of component is based on the same data. Hence, ngOnChanges is called before ngOnInit
  }

  public ngOnInit(): void {
    this.isCpiEditable = this._isCpiEditable();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.multiCountrySurveyData?.currentValue) {
      this._multiCountrySurveyDataCommand$.next(this.multiCountrySurveyData);
    }

    if (changes.multiCountrySpvChildSurveys?.currentValue) {
      this._multiCountrySpvChildSurveysCommand$.next(
        this.multiCountrySpvChildSurveys
      );
    }
  }

  private _handleFilterCountries(): void {
    this._subscriptions.add(
      merge(
        this._multiCountrySurveyDataCommand$,
        this._multiCountrySpvChildSurveysCommand$
      )
        .pipe(
          filter((data) => !!data.length),
          switchMap((data) => {
            if (this.countries.length) {
              return of(this.countries).pipe(
                map(
                  (countries) =>
                    <[typeof data, CountryInterface[]]>[data, countries]
                )
              );
            }
            return this._commonService.getCountries().pipe(
              tap((countries) => (this.countries = countries)),
              map(
                (countries) =>
                  <[typeof data, CountryInterface[]]>[data, countries]
              )
            );
          }),
          map(([data, countries]) =>
            this._filterCountries<(typeof data)[0]>(deepCopy(countries), data)
          )
        )
        .subscribe((filtered) => {
          this.filteredCountries = filtered;
        })
    );
  }

  private _filterCountries<
    T extends { country_code: string; language_code: string }
  >(countries: CountryInterface[], data: T[]): CountryInterface[] {
    return countries.filter((currentCountry) => {
      currentCountry.lang = currentCountry.lang.filter(
        (currentLanguage) =>
          !data.find(
            (mcSurvey) =>
              mcSurvey.country_code === currentCountry.short_Code &&
              mcSurvey.language_code === currentLanguage.transalte_code
          )
      );
      return !!currentCountry.lang.length;
    });
  }

  private _isCpiEditable(): boolean {
    if (this._router.url.includes('launch-survey')) {
      return false;
    }

    if (
      this._auth.buyerConfig?.enableTargetMargin &&
      this._auth.isLoggedInAsServiceBuyer()
    ) {
      return false;
    }

    return !!this._auth.buyerConfig?.overridCmp;
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
